/** Native Google / Apple Maps deep links — no paid API keys, no tile/CDN scraping. */

export type MapsPlace = {
  name: string;
  lat: number;
  lng: number;
  /** Prefer business-friendly search text, e.g. "Puelo Specialty Coffee Bar Vitacura Chile" */
  mapsQuery?: string;
  query?: string;
  /** Google Place ID when known (optional; we have none yet) */
  placeId?: string;
};

export type MapsLinks = {
  google: string;
  apple: string;
  appleHttps: string;
  appleNative: string;
  directions: string;
  /** Preferred link for photo/name clicks (Apple on iPhone/iPad, else Google). */
  primary: string;
  appleFirst: boolean;
};

export function prefersAppleMaps(
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
): boolean {
  return /iPhone|iPad|iPod/i.test(ua);
}

function queryHint(place: MapsPlace): string {
  return (place.mapsQuery ?? place.query ?? place.name).trim();
}

/**
 * Google Maps Place/search deep link.
 * Never append `@lat,lng` to `query` for named places — that opens raw coords
 * instead of Place UI (photos/reviews). Prefer Places Search API URLs; with
 * placeId use query_place_id and omit ll.
 */
export function googleMapsUrl(place: MapsPlace): string {
  const hint = queryHint(place);
  const placeId = place.placeId?.trim();
  const hasCoords = Number.isFinite(place.lat) && Number.isFinite(place.lng);
  const bareCoords =
    hasCoords && hint && hint === `${place.lat},${place.lng}`;

  // Named / mapsQuery search — never append @lat,lng to query.
  if (hint && !bareCoords) {
    const params = new URLSearchParams({
      api: "1",
      query: hint,
    });
    if (placeId) {
      params.set("query_place_id", placeId);
      // Prefer omitting ll when using query_place_id.
    }
    return `https://www.google.com/maps/search/?${params.toString()}`;
  }

  // Bare lat,lng query only when there is no name/mapsQuery.
  if (hasCoords) {
    const params = new URLSearchParams({
      api: "1",
      query: `${place.lat},${place.lng}`,
    });
    if (placeId) {
      params.set("query_place_id", placeId);
    }
    return `https://www.google.com/maps/search/?${params.toString()}`;
  }

  if (placeId) {
    const params = new URLSearchParams({
      api: "1",
      query: place.name.trim() || "place",
      query_place_id: placeId,
    });
    return `https://www.google.com/maps/search/?${params.toString()}`;
  }

  return "https://www.google.com/maps/";
}

/** Apple Maps https fallback (desktop / Android / when maps:// is blocked). */
export function appleMapsHttpsUrl(place: MapsPlace): string {
  const q = queryHint(place) || place.name;
  const params = new URLSearchParams({
    q,
    ll: `${place.lat},${place.lng}`,
    z: "16",
  });
  return `https://maps.apple.com/?${params.toString()}`;
}

/** Apple Maps native scheme — opens Maps.app on iPhone/iPad. */
export function appleMapsNativeUrl(place: MapsPlace): string {
  const q = queryHint(place) || place.name;
  const params = new URLSearchParams({
    q,
    ll: `${place.lat},${place.lng}`,
  });
  return `maps://?${params.toString()}`;
}

/** Prefer native scheme on Apple devices; https elsewhere. */
export function appleMapsUrl(place: MapsPlace): string {
  return prefersAppleMaps() ? appleMapsNativeUrl(place) : appleMapsHttpsUrl(place);
}

/** Compact Google directions deep link. */
export function googleDirectionsUrl(place: MapsPlace): string {
  if (!Number.isFinite(place.lat) || !Number.isFinite(place.lng)) {
    return googleMapsUrl(place);
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${place.lat},${place.lng}`,
  )}`;
}

/** One-shot helper for UI: Google + Apple (+ directions) for a pin-like place. */
export function mapsLinks(pin: MapsPlace): MapsLinks {
  const appleFirst = prefersAppleMaps();
  const google = googleMapsUrl(pin);
  const appleHttps = appleMapsHttpsUrl(pin);
  const appleNative = appleMapsNativeUrl(pin);
  const apple = appleFirst ? appleNative : appleHttps;
  return {
    google,
    apple,
    appleHttps,
    appleNative,
    directions: googleDirectionsUrl(pin),
    primary: appleFirst ? apple : google,
    appleFirst,
  };
}

function placeFromPin(p: {
  name: string;
  lat: number;
  lng: number;
  mapsQuery?: string;
  placeId?: string;
}): MapsPlace {
  return {
    name: p.name,
    lat: p.lat,
    lng: p.lng,
    mapsQuery: p.mapsQuery,
    placeId: p.placeId,
  };
}

/**
 * Open several day pins in Google Maps as a multi-stop directions route
 * (overview of the day's places). Falls back to a single search when ≤1 pin.
 */
export function googleDayMapsUrl(
  pins: Array<{
    name: string;
    lat: number;
    lng: number;
    mapsQuery?: string;
    placeId?: string;
  }>,
  label = "Day places",
): string {
  const usable = pins.filter(
    (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng),
  );
  if (usable.length === 0) {
    return `https://maps.google.com/?q=${encodeURIComponent(label)}`;
  }
  if (usable.length === 1) {
    return googleMapsUrl(placeFromPin(usable[0]));
  }
  // Path-style multi-stop dir URL opens the Google Maps app with the route.
  const path = usable.map((p) => `${p.lat},${p.lng}`).join("/");
  return `https://www.google.com/maps/dir/${path}`;
}

/**
 * Apple Maps day overview — center on the day's pins with a search query.
 * (Apple has no public multi-pin deep link without MapKit.)
 */
export function appleDayMapsUrl(
  pins: Array<{
    name: string;
    lat: number;
    lng: number;
    mapsQuery?: string;
    placeId?: string;
  }>,
  label = "Day places",
): string {
  const usable = pins.filter(
    (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng),
  );
  if (usable.length === 0) {
    const params = new URLSearchParams({ q: label });
    return prefersAppleMaps()
      ? `maps://?${params.toString()}`
      : `https://maps.apple.com/?${params.toString()}`;
  }
  if (usable.length === 1) {
    return appleMapsUrl(placeFromPin(usable[0]));
  }
  const lat =
    usable.reduce((s, p) => s + p.lat, 0) / usable.length;
  const lng =
    usable.reduce((s, p) => s + p.lng, 0) / usable.length;
  const names = usable
    .slice(0, 4)
    .map((p) => p.name)
    .join(", ");
  const q = `${label}: ${names}`.slice(0, 120);
  const params = new URLSearchParams({
    q,
    ll: `${lat},${lng}`,
    z: "12",
  });
  return prefersAppleMaps()
    ? `maps://?${params.toString()}`
    : `https://maps.apple.com/?${params.toString()}`;
}

export function googleMapsAreaUrl(opts: {
  name?: string;
  lat?: number;
  lng?: number;
  query?: string;
}): string {
  if (opts.query || opts.name) {
    return googleMapsUrl({
      name: opts.name ?? opts.query ?? "Map",
      lat: opts.lat ?? 0,
      lng: opts.lng ?? 0,
      mapsQuery: opts.query ?? opts.name,
    });
  }
  if (opts.lat != null && opts.lng != null) {
    return `https://maps.google.com/?q=${encodeURIComponent(
      `${opts.lat},${opts.lng}`,
    )}&ll=${opts.lat},${opts.lng}`;
  }
  return "https://maps.google.com/";
}

export function appleMapsAreaUrl(opts: {
  name?: string;
  lat?: number;
  lng?: number;
  query?: string;
}): string {
  const q = (opts.query ?? opts.name ?? "Map").trim();
  const params = new URLSearchParams({ q });
  if (opts.lat != null && opts.lng != null) {
    params.set("ll", `${opts.lat},${opts.lng}`);
    params.set("z", "12");
  }
  return prefersAppleMaps()
    ? `maps://?${params.toString()}`
    : `https://maps.apple.com/?${params.toString()}`;
}
